import { queryOptions, useQuery, type QueryClient } from '@tanstack/react-query'

import { marketEndpoints } from '@/api/endpointCache'
import { companyKeys } from '@/features/company/api/keys'
import { LIVE_MAX_LIMIT } from '@/features/market/api/marketApi'
import { money, type Money } from '@/lib/money'

/** One stock of the same sector, as the rail and Compare's default pick draw it. */
export interface SectorPeer {
  symbol: string
  name: string
  /** `null` when the live table has no print for it. */
  ltp: Money | null
  changePercent: Money | null
}

/**
 * The company's same-sector stocks, busiest first: mm-be publishes no per-stock peer list
 * (`asks/cp1.md` A2), so the owner's proxy (ruling C3, 2026-10-29) is the sector's members ranked
 * by today's turnover. The order is the sector heatmap's turnover sizing; the price and the move
 * are the live table's, read for the same sector. Both reads go through the shared endpoint cache,
 * so a page that already holds either costs nothing more.
 *
 * The company itself is never among them, and a member with no turnover figure sorts last.
 */
export async function sectorPeers(
  sectorSymbol: string,
  own: string,
  limit: number,
  client?: QueryClient,
): Promise<SectorPeer[]> {
  const api = marketEndpoints(client)
  const [tiles, live] = await Promise.all([
    api.heatmap(sectorSymbol, 'turnover'),
    api.live(LIVE_MAX_LIMIT, sectorSymbol),
  ])
  const prints = new Map(live.rows.map((row) => [row.key, row]))
  const turnover = (size: string | null) => (size === null ? -1 : Number(size))
  return [...tiles.rows]
    .filter((tile) => tile.key.toUpperCase() !== own.toUpperCase())
    .sort((a, b) => turnover(b.size) - turnover(a.size))
    .slice(0, limit)
    .map((tile) => {
      const print = prints.get(tile.key)
      const change = print?.change_percent ?? tile.change_percent
      return {
        symbol: tile.key,
        name: tile.name,
        ltp: print === undefined ? null : money(print.ltp),
        changePercent: change === null ? null : money(change),
      }
    })
}

export const sectorPeersQueryOptions = (symbol: string, sectorSymbol: string, limit: number) =>
  queryOptions<SectorPeer[]>({
    queryKey: [...companyKeys.detail(symbol), 'sector-peers', { sectorSymbol, limit }] as const,
    queryFn: ({ client }) => sectorPeers(sectorSymbol, symbol, limit, client),
  })

/** `sectorPeers` for the page's company. Off while the company has no sector on record. */
export const useSectorPeers = (symbol: string, sectorSymbol: string | null, limit: number) =>
  useQuery({
    ...sectorPeersQueryOptions(symbol, sectorSymbol ?? '', limit),
    enabled: sectorSymbol !== null,
  })
